import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiType, ApiRequestOptions, ApiConfig, ApiClientInterface, TokenProvider } from './ApiClient.types';
import { CLIENT_PLATFORM_HEADER, DEFAULT_AXIOS_CONFIG } from './ApiClient.constants';
import {
  extractApiConfig,
  getEndpointUrl,
  getBaseUrl,
  getClientPlatform,
  prepareRequestHeaders,
} from './ApiClient.utils';

export class ApiClient implements ApiClientInterface {
  private axiosInstance: AxiosInstance;
  private currentConfig: ApiConfig = {};
  private token?: string;
  private tokenProvider?: TokenProvider;

  constructor(axiosConfig?: AxiosRequestConfig) {
    this.axiosInstance = axios.create(axiosConfig || DEFAULT_AXIOS_CONFIG);
  }

  setStore(store: any): void {
    const state = store.getState();
    this.currentConfig = extractApiConfig(state);
  }

  getToken(): string | undefined {
    return this.token;
  }

  setToken(token: string): void {
    this.token = token;
  }

  setTokenProvider(provider?: TokenProvider): void {
    this.tokenProvider = provider;
  }

  clearToken(): void {
    this.token = undefined;
  }

  getCurrentConfig(): ApiConfig {
    return this.currentConfig;
  }

  private async request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    apiType: ApiType,
    options: ApiRequestOptions,
  ): Promise<AxiosResponse<T>> {
    const {
      url,
      data,
      headers,
      params,
      skipNafHeaders = false,
      withCredentials = false,
      skipPlatformHeader = false,
      signal,
    } = options;

    // For external API type, use the URL directly without base URL building
    const fullUrl =
      apiType === ApiType.external
        ? url
        : `${getBaseUrl(this.currentConfig)}/${getEndpointUrl(apiType, this.currentConfig)}/${url}`;

    const currentToken = this.tokenProvider ? await this.tokenProvider() : this.token;
    const configWithToken = { ...this.currentConfig, authToken: currentToken };

    // Skip NAF headers (auth + apim key) if requested
    const requestHeaders = skipNafHeaders ? headers || {} : prepareRequestHeaders(headers, configWithToken);
    const headersWithPlatform = skipPlatformHeader
      ? requestHeaders
      : {
          ...requestHeaders,
          [CLIENT_PLATFORM_HEADER]: getClientPlatform(),
        };

    const config: AxiosRequestConfig = {
      method,
      url: fullUrl,
      data,
      headers: headersWithPlatform,
      params,
      withCredentials,
      signal,
    };

    try {
      return this.axiosInstance.request<T>(config);
    } catch (error: any) {
      const serializedError = {
        message: error?.message || `API Error: ${url}`,
        status: error?.response?.status as number | undefined,
        data: error?.response?.data as unknown,
        url,
        method,
        params,
      };
      throw serializedError;
    }
  }

  async get<T = any>(apiType: ApiType, options: ApiRequestOptions): Promise<AxiosResponse<T>> {
    return this.request<T>('GET', apiType, options);
  }

  async post<T = any>(apiType: ApiType, options: ApiRequestOptions): Promise<AxiosResponse<T>> {
    return this.request<T>('POST', apiType, options);
  }

  async put<T = any>(apiType: ApiType, options: ApiRequestOptions): Promise<AxiosResponse<T>> {
    return this.request<T>('PUT', apiType, options);
  }

  async delete<T = any>(apiType: ApiType, options: ApiRequestOptions): Promise<AxiosResponse<T>> {
    return this.request<T>('DELETE', apiType, options);
  }

  async patch<T = any>(apiType: ApiType, options: ApiRequestOptions): Promise<AxiosResponse<T>> {
    return this.request<T>('PATCH', apiType, options);
  }

  async requestWithMethod<T = any>(
    requestMethod: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    apiType: ApiType,
    options: ApiRequestOptions,
  ): Promise<AxiosResponse<T>> {
    return this.request<T>(requestMethod, apiType, options);
  }

  // Cleanup method for testing or cleanup
  destroy(): void {
    this.currentConfig = {};
    this.token = undefined;
    this.tokenProvider = undefined;
  }
}
